import React from "react";
import { useTranslation } from "../../../i18n/LanguageContext";
import "./css/ConfirmationModal.css";

const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  details,
  confirmText = "Confirm",
  cancelText = "Cancel",
  type = "default", // 'default', 'danger', 'warning', 'info'
  showReasonInput = false,
  reasonPlaceholder = "Optional: Tell us why...",
}) => {
  const [reason, setReason] = React.useState("");
  const [isProcessing, setIsProcessing] = React.useState(false);
  const { t } = useTranslation();

  // Reset reason when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setReason("");
      setIsProcessing(false);
    }
  }, [isOpen]);

  // Close on Escape key
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen && !isProcessing) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, isProcessing, onClose]);

  const handleConfirm = async () => {
    setIsProcessing(true);
    try {
      await onConfirm(reason);
      onClose();
    } catch (error) {
      console.error("Confirmation action failed:", error);
      // Don't close modal on error, let parent component handle it
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isProcessing) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="confirmation-modal-overlay" onClick={handleBackdropClick}>
      <div className={`confirmation-modal ${type}`}>
        <div className="confirmation-modal-header">
          <div className={`confirmation-modal-icon ${type}`}>
            {type === "danger" && "⚠️"}
            {type === "warning" && "⚡"}
            {type === "info" && "ℹ️"}
            {type === "default" && "❓"}
          </div>
          <h3 className="confirmation-modal-title">{title}</h3>
        </div>

        <div className="confirmation-modal-body">
          <p className="confirmation-modal-message">{message}</p>

          {details && (
            <div className="confirmation-modal-details">
              {Array.isArray(details) ? (
                <ul>
                  {details.map((detail, index) => (
                    <li key={index}>{detail}</li>
                  ))}
                </ul>
              ) : typeof details === "string" ? (
                <p>{details}</p>
              ) : (
                details
              )}
            </div>
          )}

          {showReasonInput && (
            <div className="confirmation-modal-input">
              <label htmlFor="reason-input">
                {t("confirmModal.reasonLabel")}
              </label>
              <textarea
                id="reason-input"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={reasonPlaceholder}
                rows={3}
                disabled={isProcessing}
              />
            </div>
          )}
        </div>

        <div className="confirmation-modal-footer">
          <button
            className="confirmation-btn cancel"
            onClick={onClose}
            disabled={isProcessing}
          >
            {cancelText}
          </button>
          <button
            className={`confirmation-btn confirm ${type}`}
            onClick={handleConfirm}
            disabled={isProcessing}
          >
            {isProcessing ? t("confirmModal.processing") : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
